/**
 * Maps Cloudflare Worker / D1 analytics rows (snake_case) to UI labels.
 */

export interface QueueRow {
  id: number;
  name: string;
  number: string;
  status: string;
  attempts: number;
  created_at?: string;
  updated_at?: string;
  createdAt?: string;
  updatedAt?: string;
}

/** Row from GET /analytics → completedCalls (D1 retell_completed_calls) */
export interface CompletedCallRow {
  id: number;
  call_id: string | null;
  name: string | null;
  phone_number: string | null;
  outcome: string | null;
  status: string | null;
  call_status: string | null;
  meeting_sent: number | boolean | null;
  call_failed: number | boolean | null;
  call_unsuccessful: number | boolean | null;
  overall_call: string | null;
  total_duration_seconds: number | null;
  llm_token_usage_average: number | null;
  completed_at: string | null;
  failed_at: string | null;
  created_at?: string;
  updated_at?: string;
  createdAt?: string;
  updatedAt?: string;
  call_outcome?: string | null;
  call_summary?: string | null;
  user_sentiment?: string | null;
  meeting_status?: string | null;
  failure_reason?: string | null;
  next_action?: string | null;
}

const SUCCESS_OUTCOMES = new Set([
  'completed_success',
  'rescheduled',
  'voicemail_left',
  'other',
]);

const TERMINAL_FAIL_OUTCOMES = new Set(['completed_no_interest', 'bad_number']);

function truthy(v: unknown): boolean {
  return v === true || v === 1 || v === '1';
}

function humanizeEnum(s: string): string {
  return s
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

export function truncate(text: string, max: number): string {
  const t = text.trim();
  if (t.length <= max) return t;
  return `${t.slice(0, max - 1)}…`;
}

/** Used for success-rate and "positive" KPIs */
export function isSuccessfulCompletedCall(c: CompletedCallRow): boolean {
  const st = (c.status || '').toLowerCase();
  if (st === 'completed') return true;
  if (st === 'requeued' || st === 'unsuccessful') return false;

  if (c.call_outcome && SUCCESS_OUTCOMES.has(c.call_outcome)) return true;
  if (c.meeting_status === 'scheduled' || c.meeting_status === 'rescheduled') return true;
  if (truthy(c.meeting_sent)) return true;

  if (c.call_outcome && TERMINAL_FAIL_OUTCOMES.has(c.call_outcome)) return false;
  if (truthy(c.call_failed) || truthy(c.call_unsuccessful)) return false;

  return false;
}

/** Badge label for list / recent activity */
export function completedCallStatusLabel(c: CompletedCallRow): string {
  const st = (c.status || '').toLowerCase();
  if (st === 'requeued') return 'Requeued';
  if (st === 'unsuccessful') return 'Failed';
  if (st === 'completed') return 'Completed';
  if (truthy(c.call_failed)) return 'Failed';
  if (truthy(c.call_unsuccessful)) return 'Failed';
  return c.status || 'Done';
}

/**
 * Primary subtitle line: summary, or humanized outcome, or legacy overall_call.
 */
export function completedCallResultLine(c: CompletedCallRow): string {
  if (c.call_summary && String(c.call_summary).trim()) {
    return truncate(String(c.call_summary), 120);
  }
  if (c.call_outcome) return humanizeEnum(c.call_outcome);
  if (c.overall_call && String(c.overall_call).trim()) {
    return truncate(String(c.overall_call), 120);
  }
  if (c.outcome) return humanizeEnum(String(c.outcome));
  return '—';
}

/** Compact line for dashboard chips */
export function completedCallChipLine(c: CompletedCallRow): string {
  const parts: string[] = [];
  if (c.call_outcome) parts.push(humanizeEnum(c.call_outcome));
  if (c.user_sentiment) parts.push(humanizeEnum(c.user_sentiment));
  if (c.meeting_status) parts.push(`Meeting: ${humanizeEnum(c.meeting_status)}`);
  if (parts.length) return truncate(parts.join(' · '), 90);
  return completedCallResultLine(c);
}

export function completedTimestamp(c: CompletedCallRow): string {
  return (
    c.completed_at ||
    c.failed_at ||
    c.created_at ||
    c.createdAt ||
    ''
  );
}
